import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  // eslint-disable-next-line no-unused-vars
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

// Hooks
import { useDeleteTranscript } from '@hooks/deleteQueries';
import {
  useFetchAssignedTags,
  useFetchStudyTags,
  useFetchTranscriptList,
  useSingleInterview,
} from '@hooks/getQueries';
import { useAddStudyTag, useMarkAsCompleted, useUpdateTranscriptName } from '@hooks/postQueries';

// Components
import RenameTranscriptModal from './components/RenameTranscriptModal';
import TagLibraryModal from './components/TagLibraryModal';
import TranscriptItem from './components/TranscriptItem';
import TranscriptListHeader from './components/TranscriptListHeader';

// Utilities and hooks
import ConfirmationDialog from '@components/common/ConfirmationDialog';
import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import AssignTags from '@components/interview/Tag/AssignTags';
import Spinner from '@components/spinner/Spinner';
import usePermission from '@hooks/usePermission';
import toast from 'react-hot-toast';
import FileUpload from '../upload/FileUpload';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';

const useStyles = makeStyles(() => ({
  sidebarContainer: {
    height: 'calc(100vh - 135px)',
    display: 'flex',
    flexDirection: 'column',
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e8e8e8',
  },
  fieldList: {
    flex: 1,
    padding: '0',
    paddingBottom: '80px',
    overflow: 'auto',
    '& > div': {
      borderRadius: '5px',
    },
  },
}));

const TranscriptList = () => {
  const classes = useStyles();
  const { transcriptId, id } = useParams();
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [headerAnchorEl, setHeaderAnchorEl] = useState(null);
  const [currentTranscript, setCurrentTranscript] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRenameTranscriptModal, setOpenRenameTranscriptModal] = useState(false);
  const [transcriptIdState, setTranscriptIdState] = useState(null);
  const editTranscriptNameRef = useRef(null);
  const [openTagModal, setOpenTagModal] = useState(false);
  const [openTagLibraryModal, setOpenTagLibraryModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryAssignedTag] = useState('');
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [expandedParents, setExpandedParents] = useState({});

  // Auto-expand parent when a derived child is the active transcript
  useEffect(() => {
    if (!transcriptId || !transcripts?.length) return;
    const activeTranscript = transcripts.find((t) => t.id === transcriptId);
    if (activeTranscript?.is_derived) {
      const rootId =
        activeTranscript.root_qualitative_item_id ||
        activeTranscript.derived_from_qualitative_item_id;
      if (rootId && !expandedParents[rootId]) {
        setExpandedParents((prev) => ({ ...prev, [rootId]: true }));
      }
    }
  }, [transcriptId]);
  const [showReinviteModal, setShowReinviteModal] = useState(false);
  const [reinviteData, setReinviteData] = useState(null);
  const [reinviteLoading, setReinviteLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { mutate: markAsCompleted, isPending: isMarkingAsCompletedPending } = useMarkAsCompleted();
  const [selectedTags, setSelectedTags] = useState(() => {
    try {
      const savedTags = localStorage.getItem(`transcript-filter-tags-${id}`);
      return savedTags ? JSON.parse(savedTags) : [];
    } catch (error) {
      console.warn('Failed to load saved tag filters:', error);
      return [];
    }
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const getInitialPageSize = () => {
    const itemHeight = 70; // average item height
    const viewportHeight = window.innerHeight;
    return Math.ceil(viewportHeight / itemHeight) + 5; // +5 buffer
  };

  // Persist selected tags to localStorage whenever they change
  const handleTagFilterChange = useCallback(
    (newTags) => {
      setSelectedTags(newTags);
      try {
        if (newTags && newTags.length > 0) {
          localStorage.setItem(`transcript-filter-tags-${id}`, JSON.stringify(newTags));
        } else {
          localStorage.removeItem(`transcript-filter-tags-${id}`);
        }
      } catch (error) {
        console.warn('Failed to save tag filters:', error);
      }
    },
    [id],
  );

  // State to track if any transcripts are processing (for polling)
  const [hasProcessingTranscripts, setHasProcessingTranscripts] = useState(false);

  // Data fetching - enable polling every 5 seconds when transcripts are processing
  const {
    data: transcriptPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isPendingTranscripts,
  } = useFetchTranscriptList(
    id,
    selectedTags,
    getInitialPageSize(),
    {
      refetchInterval: hasProcessingTranscripts ? 5000 : false,
    },
    statusFilter || undefined,
    typeFilter || undefined,
  );

  // Update processing state when transcript data changes
  useEffect(() => {
    if (!transcriptPages?.pages) {
      setHasProcessingTranscripts(false);
      return;
    }
    const isProcessing = transcriptPages.pages.some((page) =>
      page.transcripts?.some(
        (t) => t.status === 'processing' || t.status === 'transcribing' || t.status === 'uploading',
      ),
    );
    setHasProcessingTranscripts(isProcessing);
  }, [transcriptPages]);

  const { data: interview } = useSingleInterview(id);
  const {
    data: { studyPermission },
  } = usePermission({ study: interview });
  const ownerEmail = useMemo(() => {
    if (!interview) return null;

    const ownerById = interview?.members?.find(
      (member) => member?.user_id === interview?.owner_id || member?.id === interview?.owner_id,
    );
    const ownerByRole = interview?.members?.find((member) => member?.role === 'owner');

    return interview?.owner_email || ownerById?.user_email || ownerByRole?.user_email || null;
  }, [interview]);

  const deleteDisabledTooltip = ownerEmail
    ? `You do not have permissions to perform this action. Please contact the study owner: ${ownerEmail}`
    : 'You do not have permissions to perform this action. Please contact the study owner.';

  const permissionDisabledTooltip = deleteDisabledTooltip;

  const renderActionMenuItem = ({ label, onClick, disabled, tooltip, color = 'inherit' }) => {
    if (!disabled) {
      return (
        <MenuItem onClick={onClick} sx={color === 'inherit' ? undefined : { color }}>
          <ListItemText>{label}</ListItemText>
        </MenuItem>
      );
    }

    return (
      <Tooltip title={tooltip} placement="left">
        <span>
          <MenuItem disabled sx={color === 'inherit' ? undefined : { color }}>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
    );
  };

  // Tag-related data fetching
  const { data: tagsList, isFetching: isFetchingTags } = useFetchStudyTags({
    studyId: id,
    searchQuery,
  });
  const { data: assignedTagList } = useFetchAssignedTags(id, searchQueryAssignedTag);

  // Flatten all transcript pages into a single array
  const transcripts = useMemo(() => {
    if (!transcriptPages?.pages) return [];
    return transcriptPages.pages.flatMap((page) => page.transcripts || []);
  }, [transcriptPages]);

  // Auto-select first transcript on initial load and handle deleted transcripts
  useEffect(() => {
    // Don't navigate if there are no transcripts
    if (transcripts.length === 0) {
      setInitialLoad(false);
      // If there's a transcriptId in URL but no transcripts, clear it
      if (transcriptId) {
        navigate(`/interview/${id}/transcripts`, { replace: true });
      }
      return;
    }

    if (initialLoad && transcripts.length > 0 && !transcriptId) {
      // Only auto-select if there are transcripts and no transcript is currently selected
      navigate(`/interview/${id}/transcripts/${transcripts[0].id}`, {
        replace: true,
        state: { transcript: transcripts[0] },
      });
      setInitialLoad(false);
    } else if (transcripts.length > 0) {
      // If we have transcripts but no selection, and it's not the initial load
      const transcriptExists = transcripts.some((t) => t.id === transcriptId);
      if (!transcriptExists && transcripts[0]) {
        navigate(`/interview/${id}/transcripts/${transcripts[0].id}`, {
          replace: true,
          state: { transcript: transcripts[0] },
        });
      }
      setInitialLoad(false);
    }
  }, [transcripts, transcriptId, id, initialLoad, navigate]);

  // Form setup for rename functionality
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
    },
  });

  // Custom hooks
  const { mutateAsync: deleteTranscript, isPending } = useDeleteTranscript();
  const { mutate: updateTranscriptName, isPending: isUpdatingTranscriptName } =
    useUpdateTranscriptName();
  const { mutate: addStudyTag, isPending: isAddingStudyTag } = useAddStudyTag();

  // Infinite scroll hook
  const { handleScroll } = useInfiniteScroll(fetchNextPage, hasNextPage, isFetchingNextPage);

  // Event handlers - memoized with useCallback for performance
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  }, []);

  const handleMenuClick = useCallback((event, transcript) => {
    setAnchorEl(event.currentTarget);
    setCurrentTranscript(transcript);
  }, []);

  const handleSubmenuClose = useCallback(() => {
    setSubmenuAnchorEl(null);
  }, []);

  const handleRename = useCallback(() => {
    if (currentTranscript) {
      setTranscriptIdState(currentTranscript.id);
      setValue('name', currentTranscript.name || '');
      setOpenRenameTranscriptModal(true);
      handleMenuClose();
    }
  }, [currentTranscript, handleMenuClose, setValue]);

  const handleAssignTags = useCallback(() => {
    if (currentTranscript) {
      setTranscriptIdState(currentTranscript.id);
      setOpenTagModal(true);
      handleMenuClose();
    }
  }, [currentTranscript, handleMenuClose]);

  const handleReinvite = useCallback(
    async (refresh = false) => {
      if (!currentTranscript && !reinviteData) return;
      const tId = currentTranscript?.id || reinviteData?.transcript_id;
      setReinviteLoading(true);
      handleMenuClose();
      setShowReinviteModal(true);
      try {
        const { getResumeLink } = await import('@services/interview');
        const data = await getResumeLink({ studyId: id, transcriptId: tId, refresh });
        setReinviteData(data);
      } catch (err) {
        console.error('Failed to get resume link:', err);
      } finally {
        setReinviteLoading(false);
      }
    },
    [currentTranscript, reinviteData, id, handleMenuClose],
  );

  const handleDownloadText = useCallback(() => {
    // TODO: Implement download text functionality
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDownloadCsv = useCallback(() => {
    // TODO: Implement download CSV functionality
    handleMenuClose();
  }, [handleMenuClose]);

  const handleDelete = useCallback((transcript) => {
    setCurrentTranscript(transcript);
    setOpenDeleteDialog(true);
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
  }, []);

  const handleHeaderPopoverOpen = useCallback((e) => {
    setHeaderAnchorEl(e.currentTarget);
  }, []);

  const handleHeaderPopoverClose = useCallback(() => {
    setHeaderAnchorEl(null);
  }, []);

  const getTranscriptName = useCallback(
    (transcriptId) => {
      const index = transcripts?.findIndex((t) => t.id === transcriptId);
      return index !== -1 ? index + 1 : 1;
    },
    [transcripts],
  );

  const handleConfirmDeleteTranscript = useCallback(async () => {
    const isCurrentTranscript = currentTranscript?.id === transcriptId;

    await deleteTranscript(currentTranscript?.id, {
      onSuccess: () => {
        setOpenDeleteDialog(false);

        setTimeout(() => {
          if (isCurrentTranscript) {
            // Get fresh transcript list after deletion
            const updatedTranscripts = transcripts.filter((t) => t.id !== currentTranscript?.id);

            if (updatedTranscripts.length > 0) {
              const nextTranscript = updatedTranscripts[0];
              setCurrentTranscript(nextTranscript);
              navigate(`/interview/${id}/transcripts/${nextTranscript.id}`, {
                replace: true,
                state: { transcript: nextTranscript },
              });
            } else {
              setCurrentTranscript(null);
              navigate(`/interview/${id}/transcripts`, { replace: true });
            }
          }
        }, 0);
      },
    });
  }, [currentTranscript, deleteTranscript, transcriptId, transcripts, navigate, id]);

  // Rename modal handlers
  const handleCloseRenameTranscriptModal = useCallback(() => {
    setOpenRenameTranscriptModal(false);
    setTranscriptIdState(null);
    setValue('name', '');
  }, [setValue]);

  const submitForm = useCallback(
    () =>
      handleSubmit((data) => {
        updateTranscriptName(
          {
            payload: data,
            transcriptId: transcriptIdState,
            id,
          },
          {
            onSuccess: handleCloseRenameTranscriptModal,
          },
        );
      })(),
    [handleSubmit, updateTranscriptName, transcriptIdState, id, handleCloseRenameTranscriptModal],
  );

  // Tag modal handlers
  const handleCloseTagModal = useCallback(() => {
    setOpenTagModal(false);
    setTranscriptIdState(null);
  }, []);

  // Tag library modal handler
  const handleCloseTagLibraryModal = useCallback(() => {
    setOpenTagLibraryModal(false);
  }, []);

  // Get previously assigned tags for the current transcript
  const previouslyAssignedTags = useMemo(() => {
    if (!currentTranscript || !assignedTagList) return [];
    return currentTranscript.assigned_tags || [];
  }, [currentTranscript, assignedTagList]);

  const handleCompleteCancel = useCallback(() => {
    setShowCompleteConfirm(false);
  }, []);

  const handleMarkAsComplete = useCallback(() => {
    if (currentTranscript) {
      markAsCompleted(
        { studyId: id, transcriptId: currentTranscript.id, isCompleted: true },
        {
          onSuccess: () => {
            setShowCompleteConfirm(false);
            setAnchorEl(null);
            setSubmenuAnchorEl(null);
            toast.success('Transcript marked as completed successfully!');
          },
        },
      );
    }
  }, [currentTranscript, id]);

  const handleMarkAsInComplete = useCallback(() => {
    if (currentTranscript) {
      markAsCompleted(
        { studyId: id, transcriptId: currentTranscript.id, isCompleted: false },
        {
          onSuccess: () => {
            setShowCompleteConfirm(false);
            setAnchorEl(null);
            setSubmenuAnchorEl(null);
            toast.success('Transcript marked as incomplete successfully!');
          },
        },
      );
    }
  }, [currentTranscript, id]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Box className={classes.sidebarContainer}>
        <div className={classes.stickyHeader}>
          <TranscriptListHeader
            transcripts={transcripts}
            anchorEl={headerAnchorEl}
            onOpenPopover={handleHeaderPopoverOpen}
            onClose={handleHeaderPopoverClose}
            selectedTags={selectedTags}
            onTagFilterChange={handleTagFilterChange}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            onUploadClick={handleOpen}
          />
        </div>
        {isPendingTranscripts ? (
          <Spinner />
        ) : transcripts.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              p: 3,
            }}
          >
            <Typography variant="h6" mb={1}>
              No transcripts yet
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              Upload your interview data to get started.
            </Typography>
          </Box>
        ) : (
          <Box className={classes.fieldList} onScroll={handleScroll}>
            {(() => {
              // Group derived transcripts under their root (original) transcript
              const parentTranscripts = [];
              const derivedByRoot = {};

              transcripts?.forEach((t) => {
                if (
                  t.is_derived &&
                  (t.root_qualitative_item_id || t.derived_from_qualitative_item_id)
                ) {
                  const rootId = t.root_qualitative_item_id || t.derived_from_qualitative_item_id;
                  if (!derivedByRoot[rootId]) derivedByRoot[rootId] = [];
                  derivedByRoot[rootId].push(t);
                } else {
                  parentTranscripts.push(t);
                }
              });

              let runningIndex = 0;
              return parentTranscripts.map((transcript) => {
                const idx = runningIndex++;
                const children = derivedByRoot[transcript.id] || [];
                const hasChildren = children.length > 0;
                const isExpanded = !!expandedParents[transcript.id];

                if (!hasChildren) {
                  return (
                    <TranscriptItem
                      key={transcript.id}
                      transcript={transcript}
                      index={idx}
                      isActive={transcriptId === transcript.id}
                      getTranscriptName={getTranscriptName}
                      assignedTagsCount={transcript?.assigned_tags?.length || 0}
                      assignedTags={transcript?.assigned_tags}
                      onMenuClick={handleMenuClick}
                    />
                  );
                }

                return (
                  <Box key={transcript.id}>
                    <TranscriptItem
                      transcript={transcript}
                      index={idx}
                      isActive={transcriptId === transcript.id}
                      getTranscriptName={getTranscriptName}
                      assignedTagsCount={transcript?.assigned_tags?.length || 0}
                      assignedTags={transcript?.assigned_tags}
                      onMenuClick={handleMenuClick}
                      flatBottom
                      derivedCount={children.length}
                      derivedExpanded={isExpanded}
                      onToggleDerived={() =>
                        setExpandedParents((prev) => ({
                          ...prev,
                          [transcript.id]: !prev[transcript.id],
                        }))
                      }
                    />
                    <Collapse in={isExpanded}>
                      <Box
                        sx={{
                          pt: 0.5,
                          pb: 0.5,
                          mb: 1,
                          '& .MuiListItem-root': { mb: '4px' },
                        }}
                      >
                        {children.map((child) => (
                          <TranscriptItem
                            key={child.id}
                            transcript={child}
                            index={idx}
                            isActive={transcriptId === child.id}
                            getTranscriptName={getTranscriptName}
                            assignedTagsCount={child?.assigned_tags?.length || 0}
                            assignedTags={child?.assigned_tags}
                            onMenuClick={handleMenuClick}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              });
            })()}

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextPage && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}

        {/* Transcript Menu */}
        <Menu
          id="transcript-menu"
          open={Boolean(anchorEl)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {renderActionMenuItem({
            label: 'Rename Transcript',
            onClick: handleRename,
            disabled: !studyPermission.transcriptEdit,
            tooltip: permissionDisabledTooltip,
          })}
          {renderActionMenuItem({
            label: 'Assign Tags',
            onClick: handleAssignTags,
            disabled: !studyPermission.transcriptEdit,
            tooltip: permissionDisabledTooltip,
          })}
          {renderActionMenuItem({
            label: 'Reinvite Participant',
            onClick: () => handleReinvite(false),
            disabled: !studyPermission.transcriptEdit || !interview?.allow_participant_resume,
            tooltip: !studyPermission.transcriptEdit
              ? permissionDisabledTooltip
              : 'Participant reinvite is disabled for this study.',
          })}
          {renderActionMenuItem({
            label: 'Transcript Workflows',
            onClick: () => {
              navigate(
                `/interview/${id}/transcripts/${currentTranscript?.id}?workflow=open&workflow_transcript_id=${currentTranscript?.id}`,
              );
              handleMenuClose();
            },
            disabled: !studyPermission.transcriptEdit,
            tooltip: permissionDisabledTooltip,
          })}
          {currentTranscript?.is_complete === false &&
            renderActionMenuItem({
              label: 'Mark as Complete',
              onClick: () => setShowCompleteConfirm(true),
              disabled: !studyPermission.transcriptEdit,
              tooltip: permissionDisabledTooltip,
            })}
          {currentTranscript?.is_complete === true &&
            renderActionMenuItem({
              label: 'Mark as Incomplete',
              onClick: () => setShowCompleteConfirm(true),
              disabled: !studyPermission.transcriptEdit,
              tooltip: permissionDisabledTooltip,
            })}
          {/* <MenuItem onClick={handleSubmenuClick}>
          <ListItemText>Download </ListItemText>
          <ArrowRightIcon />
        </MenuItem> */}
          {renderActionMenuItem({
            label: 'Delete Transcript',
            onClick: () => handleDelete(currentTranscript),
            // Owner OR study admin can delete; everyone else gets the
            // greyed-out tooltip explaining who to ask.
            disabled: !studyPermission.deleteTranscripts,
            tooltip: deleteDisabledTooltip,
            color: 'error.main',
          })}
        </Menu>

        {/* Download Submenu */}
        <Menu
          anchorEl={submenuAnchorEl}
          open={Boolean(submenuAnchorEl)}
          onClose={handleSubmenuClose}
          // anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          // transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          MenuListProps={{
            onMouseEnter: () => setSubmenuAnchorEl(submenuAnchorEl),
            onMouseLeave: handleSubmenuClose,
          }}
        >
          <MenuItem onClick={handleDownloadText}>Text</MenuItem>
          <MenuItem onClick={handleDownloadCsv}>CSV</MenuItem>
        </Menu>

        {/* Delete transcript */}
        {openDeleteDialog && (
          <DeleteConfirmationDialog
            open={openDeleteDialog}
            onClose={() => {
              setOpenDeleteDialog(false);
              setCurrentTranscript(null);
            }}
            onConfirm={handleConfirmDeleteTranscript}
            isPending={isPending}
            title="Delete Transcript"
            okText={isPending ? 'Deleting...' : 'Delete'}
            content={
              <Box>
                <Typography>
                  This action cannot be undone. Are you sure you want to delete{' '}
                  <b>{currentTranscript?.name}</b>?
                </Typography>
                <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
                  Deleting this transcript removes it from Qualz. If this transcript has linked
                  audio or video, download that media before proceeding because it will no longer be
                  available from this transcript view. Any analysis items that already quoted this
                  transcript may still show those quotes until you rerun or remove the related
                  analysis outputs.
                </Typography>
              </Box>
            }
          />
        )}

        {openRenameTranscriptModal && (
          <RenameTranscriptModal
            open={openRenameTranscriptModal}
            onClose={handleCloseRenameTranscriptModal}
            onConfirm={submitForm}
            isPending={isUpdatingTranscriptName}
            transcriptId={transcriptIdState}
            register={register}
            errors={errors}
            editTranscriptNameRef={editTranscriptNameRef}
          />
        )}

        {openTagModal && (
          <AssignTags
            id={id}
            onClose={handleCloseTagModal}
            open={openTagModal}
            tags={tagsList}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            transcriptId={transcriptIdState}
            isFetchingTags={isFetchingTags}
            addStudyTag={addStudyTag}
            isAddingStudyTag={isAddingStudyTag}
            canEdit={studyPermission.transcriptEdit}
            assigned_tags={previouslyAssignedTags}
          />
        )}

        {openTagLibraryModal && (
          <TagLibraryModal
            open={openTagLibraryModal}
            onClose={handleCloseTagLibraryModal}
            id={id}
            tagsList={tagsList}
            transcriptId={transcriptIdState}
            addStudyTag={addStudyTag}
            isAddingStudyTag={isAddingStudyTag}
            isFetchingTags={isFetchingTags}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        )}
        {/* Reinvite Participant Modal */}
        <Dialog
          open={showReinviteModal}
          onClose={() => {
            setShowReinviteModal(false);
            setReinviteData(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
          >
            Reinvite Participant
            <IconButton
              size="small"
              onClick={() => {
                setShowReinviteModal(false);
                setReinviteData(null);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {reinviteLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  Getting invite link...
                </Typography>
              </Box>
            ) : reinviteData ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Share this link with the participant to let them continue the interview from where
                  they left off. The link is valid for one week from the date it was generated.
                </Typography>

                {(() => {
                  const isExpired =
                    reinviteData.expires_at && new Date(reinviteData.expires_at) < new Date();
                  const resumeUrl = `${import.meta.env.VITE_CHAT_URL}/?study_id=${reinviteData.study_id}&study_type=qualitative_study&transcript_id=${reinviteData.transcript_id}&session_id=${reinviteData.session_id}&resume=true`;
                  return (
                    <>
                      {isExpired && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          This link has expired. Click &quot;Refresh Link&quot; to generate a new
                          one.
                        </Alert>
                      )}

                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: isExpired ? '#fff5f5' : '#f8f9fa',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: isExpired ? 'error.light' : 'divider',
                          fontSize: '0.75rem',
                          wordBreak: 'break-all',
                          color: isExpired ? 'error.main' : 'text.secondary',
                          fontFamily: 'monospace',
                          mb: 1.5,
                        }}
                      >
                        {resumeUrl}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Chip
                          size="small"
                          label={
                            isExpired
                              ? 'Expired'
                              : `Expires ${new Date(reinviteData.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                          }
                          color={isExpired ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Box>
                    </>
                  );
                })()}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Tooltip title="Generate a new link (invalidates the previous one)">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => handleReinvite(true)}
                disabled={reinviteLoading}
              >
                Refresh Link
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<ContentCopyIcon />}
              disabled={!reinviteData || reinviteLoading}
              onClick={() => {
                if (reinviteData) {
                  const url = `${import.meta.env.VITE_CHAT_URL}/?study_id=${reinviteData.study_id}&study_type=qualitative_study&transcript_id=${reinviteData.transcript_id}&session_id=${reinviteData.session_id}&resume=true`;
                  navigator.clipboard.writeText(url);
                }
              }}
            >
              Copy Link
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
      <ConfirmationDialog
        open={showCompleteConfirm}
        onClose={handleCompleteCancel}
        onConfirm={currentTranscript?.is_complete ? handleMarkAsInComplete : handleMarkAsComplete}
        isPending={isMarkingAsCompletedPending}
        title={currentTranscript?.name}
        okText={'Confirm'}
        noText={'Cancel'}
        content={
          <Box>
            <Typography>
              Do you want to mark <strong>{currentTranscript?.name}</strong> as{' '}
              {currentTranscript?.is_complete ? 'incomplete' : 'completed'} ?
            </Typography>
          </Box>
        }
      />

      <Modal
        open={open}
        // onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '556px',
            // Cap the modal at 32px shy of viewport height so the
            // bottom (Done button, staged-file list, dropzone footer)
            // is never clipped off-screen (DevOps #2183). If the
            // inner FileUpload content still exceeds this — e.g.
            // many staged files — allow it to scroll inside rather
            // than pushing the box past the viewport edge.
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          <FileUpload onClose={handleClose} />
        </Box>
      </Modal>
    </>
  );
};

export default TranscriptList;
